import { THEME_NAME, useAuth } from 'lib/context';
import { useMemo } from 'react';
import { BUTTON_VARIANT } from 'react-covideo-common/dist/components/Button/types';
import { useTheme } from 'styled-components/macro';
import mobileLogoActive from 'assets/icons/logo/covideoSmallLogoActive.svg';
import cdkSmallLogoActive from 'assets/icons/logo/cdkSmallLogoActive.svg';
import cdkSmallLogoInActive from 'assets/icons/logo/cdkSmallLogoInActive.svg';
import mobileLogoInactive from 'assets/icons/logo/covideoSmallLogoInactive.svg';
import * as theme from 'lib/style/theme';
import { WHITELABEL_NAME } from 'lib/api/whitelabel/getWhiteLabel';

type CDKVariantKey =
  | 'destructiveToSecondary'
  | 'redToSecondary'
  | 'secondaryToText'
  | 'destructiveToWhite'
  | 'primaryToSecondary'
  | 'secondaryToWhite'
  | 'destructiveToText'
  | 'secondaryToTransparent'
  | 'primaryToTransparent'
  | 'destructiveToTransparent'
  | 'redToWhite'
  | 'transparentToText'
  | 'secondaryToPrimary';

type CDKVariants = {
  [K in CDKVariantKey]: BUTTON_VARIANT;
} & {
  isCdkUser: boolean;
  primaryToBlackColor: string;
  getLogoByActiveState: (isActive?: boolean) => { src: string; alt: string };
  homePath: string;
  reportsPath: string;
  usersPath: string;
  logoutIconColor: string;
  logoutTitleColor: string;
  cdkNavItemTitlesToHide: string[];
};

export function useCDK(): CDKVariants {
  const { themeName, whitelabel } = useAuth();
  const { colors } = useTheme();

  const cdkNavItemTitlesToHide = [
    'Billing',
    'Social Profiles',
    'Boards',
    'Files',
    'QR Codes',
    'Chapters',
    'Security',
    'Manage Users',
    'Groups',
    'Overlay URLs',
    'Add-ons',
    'Help Center',
    'Video Expiration',
    ...(whitelabel?.name === WHITELABEL_NAME.CDK ? ['Log Out'] : []),
  ];

  return useMemo(() => {
    const isCdkUser = themeName === THEME_NAME.CDK;

    const getLogoByActiveState = (isActive: boolean = true) => {
      return {
        src: isCdkUser
          ? isActive
            ? cdkSmallLogoActive
            : cdkSmallLogoInActive
          : isActive
            ? mobileLogoActive
            : mobileLogoInactive,
        alt: isCdkUser ? 'CDK Logo' : 'Covideo Logo',
      };
    };

    return {
      isCdkUser,
      destructiveToSecondary: isCdkUser ? 'secondary' : 'destructive',
      redToSecondary: isCdkUser ? 'secondary' : 'red',
      secondaryToText: isCdkUser ? 'text' : 'secondary',
      destructiveToWhite: isCdkUser ? 'white' : 'destructive',
      primaryToSecondary: isCdkUser ? 'secondary' : 'primary',
      secondaryToWhite: isCdkUser ? 'white' : 'secondary',
      secondaryToPrimary: isCdkUser ? 'primary' : 'secondary',
      destructiveToText: isCdkUser ? 'text' : 'destructive',
      destructiveToTransparent: isCdkUser ? 'transparent' : 'destructive',
      secondaryToTransparent: isCdkUser ? 'transparent' : 'secondary',
      primaryToTransparent: isCdkUser ? 'transparent' : 'primary',
      transparentToText: isCdkUser ? 'text' : 'transparent',
      redToWhite: isCdkUser ? 'white' : 'red',
      primaryToBlackColor: isCdkUser ? colors.black[100] : colors.primary[100],
      // this should be refactored later to use react-router paths since baseName is different for cdk users
      // change in LocationSelect.tsx
      // check useCDKStoreIdLoginGuardHardReload.tsx for example
      reportsPath: isCdkUser ? '/admin/reports/overview' : '/reports/overview',
      usersPath: isCdkUser ? '/admin/users/list' : '/users/list',
      homePath: isCdkUser ? '/admin/home' : '/home',
      // ***
      logoutIconColor: isCdkUser ? 'rgba(0, 27, 83, 0.4)' : theme.palette.red40,
      logoutTitleColor: isCdkUser
        ? theme.palette.primaryDarkBlue
        : 'rgb(232, 76, 61)',
      getLogoByActiveState,
      cdkNavItemTitlesToHide,
    };
  }, [themeName]);
}
